import { useState, useEffect } from 'react';
import { Activity, BarChart3, Trophy, Gift, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { fetchEvaluations, checkPrizeClaim, claimPrize } from '@/lib/api/simulation';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

interface ProgressTabProps {
  agentId: string;
  userId: string;
  goalScore: number;
}

export function ProgressTab({ agentId, userId, goalScore }: ProgressTabProps) {
  const [prizeClaim, setPrizeClaim] = useState<any>(null);
  const [isClaimingPrize, setIsClaimingPrize] = useState(false);
  const [checkingClaim, setCheckingClaim] = useState(true);

  const { data: evaluations, isLoading } = useQuery({
    queryKey: ['progress', agentId, userId],
    queryFn: () => fetchEvaluations(agentId, userId),
  });

  // Check prize claim status when data loads
  useEffect(() => {
    const checkClaim = async () => {
      if (evaluations && evaluations.length > 0) {
        const bestScoreValue = Math.max(...evaluations.map(e => e.overall_score || 0));
        if (bestScoreValue >= goalScore) {
          setCheckingClaim(true);
          const claim = await checkPrizeClaim(userId, agentId);
          setPrizeClaim(claim);
          setCheckingClaim(false);
        } else {
          setCheckingClaim(false);
        }
      } else {
        setCheckingClaim(false);
      }
    };
    
    checkClaim();
  }, [evaluations, goalScore, userId, agentId]);

  const handleClaimPrize = async () => {
    if (isClaimingPrize || prizeClaim) return;
    
    setIsClaimingPrize(true);
    
    // Find the evaluation with the best score
    const bestScoreValue = Math.max(...(evaluations?.map(e => e.overall_score || 0) || []));
    const bestEvaluation = evaluations?.find(e => e.overall_score === bestScoreValue);
    
    if (!bestEvaluation) {
      toast.error('Unable to find evaluation data');
      setIsClaimingPrize(false);
      return;
    }
    
    const result = await claimPrize({
      userId: userId,
      agentId: agentId,
      sessionId: bestEvaluation.id,
      evaluationId: bestEvaluation.id,
      goalScore: goalScore,
    });
    
    if (result.success && result.claim) {
      setPrizeClaim(result.claim);
    }
    
    setIsClaimingPrize(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  if (!evaluations || evaluations.length === 0) {
    return (
      <div className="text-center p-12">
        <BarChart3 size={64} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-xl text-muted-foreground mb-2">No progress data yet</p>
        <p className="text-muted-foreground">Complete more simulations to see your progress!</p>
      </div>
    );
  }

  // Calculate stats
  const sessionsCompleted = evaluations.length;
  const avgScore = Math.round(
    evaluations.reduce((sum, e) => sum + (e.overall_score || 0), 0) / evaluations.length
  );
  const bestScore = Math.max(...evaluations.map(e => e.overall_score || 0));
  const latestScore = evaluations[0]?.overall_score || 0;

  // Transform for chart
  const chartData = evaluations.slice().reverse().map((item, index) => ({
    session: index + 1,
    date: new Date(item.evaluated_at).toLocaleDateString(),
    overall: item.overall_score,
    communication: item.detailed_evaluation?.criteria_scores?.find(c => 
      c.criterion.toLowerCase().includes('communication')
    )?.score || 0,
    knowledge: item.detailed_evaluation?.criteria_scores?.find(c => 
      c.criterion.toLowerCase().includes('knowledge')
    )?.score || 0,
  }));

  const getPerformanceBadgeClass = (performance: string) => {
    if (performance === 'Excellent') return 'bg-success/10 text-success';
    if (performance === 'Good') return 'bg-primary/10 text-primary';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="text-primary" size={24} />
            <span className="text-muted-foreground text-sm">Sessions Completed</span>
          </div>
          <div className="text-4xl font-bold text-foreground">{sessionsCompleted}</div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="text-primary" size={24} />
            <span className="text-muted-foreground text-sm">Average Score</span>
          </div>
          <div className="text-4xl font-bold text-foreground">{avgScore}</div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="text-primary" size={24} />
            <span className="text-muted-foreground text-sm">Best Score</span>
          </div>
          <div className="text-4xl font-bold text-success">{bestScore}</div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="text-primary" size={24} />
            <span className="text-muted-foreground text-sm">Latest Score</span>
          </div>
          <div className="text-4xl font-bold text-foreground">{latestScore}</div>
        </Card>
      </div>

      {/* Performance Chart */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-foreground mb-6">Performance Over Time</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="session" 
              label={{ value: 'Session Number', position: 'insideBottom', offset: -5 }}
              stroke="hsl(var(--foreground))"
            />
            <YAxis 
              label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
              domain={[0, 100]}
              stroke="hsl(var(--foreground))"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="overall" 
              stroke="hsl(var(--primary))" 
              strokeWidth={3}
              name="Overall Score"
            />
            <Line 
              type="monotone" 
              dataKey="communication" 
              stroke="hsl(var(--success))" 
              strokeWidth={2}
              name="Communication"
            />
            <Line 
              type="monotone" 
              dataKey="knowledge" 
              stroke="hsl(var(--info))" 
              strokeWidth={2}
              name="Knowledge"
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Goal Achievement & Prize Claiming */}
      {bestScore >= goalScore ? (
        // ELIGIBLE - Show claim section
        <Card className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="text-success" size={32} />
            <h3 className="text-2xl font-bold text-foreground">Goal Achievement</h3>
          </div>
          
          {/* Progress Bar */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1">
              <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-success h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                  style={{ width: '100%' }}
                >
                  <span className="text-white text-xs font-bold">{bestScore}</span>
                </div>
              </div>
            </div>
            <span className="text-xl font-bold text-foreground">{goalScore}</span>
          </div>

          {/* Achievement Details */}
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <p className="text-success font-semibold text-lg mb-2">
              🎉 Congratulations! You've achieved your goal score!
            </p>
            <p className="text-muted-foreground">
              Best Score: <span className="font-bold text-success">{bestScore}</span> | 
              Goal: <span className="font-bold">{goalScore}</span>
            </p>
          </div>

          {/* Claim Status */}
          {checkingClaim ? (
            <div className="flex justify-center py-4">
              <Loader2 className="animate-spin text-primary" size={32} />
            </div>
          ) : prizeClaim ? (
            // ALREADY CLAIMED
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-success">
                <Check size={24} />
                <span className="font-semibold text-lg">Prize Claimed</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Claimed on {new Date(prizeClaim.claimed_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              {prizeClaim.prize_description && (
                <p className="text-sm text-foreground">
                  <span className="font-medium">Prize:</span> {prizeClaim.prize_description}
                </p>
              )}
              <Button 
                size="lg" 
                disabled 
                className="w-full"
              >
                Already Claimed
              </Button>
            </div>
          ) : (
            // NOT CLAIMED - Show claim button
            <div className="space-y-4">
              <Button 
                size="lg" 
                onClick={handleClaimPrize}
                disabled={isClaimingPrize}
                className="w-full bg-success hover:bg-success/90 text-white font-bold text-lg h-14"
              >
                {isClaimingPrize ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Gift className="mr-2" size={24} />
                    Claim Your Reward
                  </>
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Recognition for your outstanding achievement
              </p>
            </div>
          )}
        </Card>
      ) : (
        // NOT ELIGIBLE - Show progress
        <div className="bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/20 rounded-xl p-6">
          <h3 className="text-xl font-bold text-foreground mb-4">Goal Achievement</h3>
          <div className="flex items-center gap-4 mb-3">
            <div className="flex-1">
              <div className="w-full bg-card rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-primary h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                  style={{ width: `${Math.min((bestScore / goalScore) * 100, 100)}%` }}
                >
                  {bestScore >= goalScore * 0.5 && (
                    <span className="text-white text-xs font-bold">{bestScore}</span>
                  )}
                </div>
              </div>
            </div>
            <span className="text-2xl font-bold text-foreground">{goalScore}</span>
          </div>
          <p className="text-foreground">
            You're {goalScore - bestScore} point{goalScore - bestScore !== 1 ? 's' : ''} away from your goal. Keep going!
          </p>
        </div>
      )}
    </div>
  );
}
